import { Box, Container } from '@mui/material'
import dayjs from 'dayjs'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import 'dayjs/locale/es'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import CustomSnackBar from '../../../../shared/components/CustomSnackBar'
import DetailHeroPanel from '../../../../shared/components/DetailHeroPanel'
import DetailInfoPanel from '../../../../shared/components/DetailInfoPanel'
import ScreenLoading from '../../../../shared/components/ScreenLoading'
import ButtonBack from '../../../../shared/components/buttons/ButtonBack'
import VerifyAccessRoute from '../../../../shared/components/routes/VerifyAccessRoute'
import useScreenWidth from '../../../../shared/hooks/useScreenWidth'
import { VipContainerHeroDetail } from '../../../../shared/styles/VipStyles'
import SeminarModal from '../components/SeminarModal'
import TicketModal from '../components/TicketModal'
import ContentTabs from '../components/detail/ContentTabs'
import CoverSection from '../components/detail/CoverSection'
import CustomEmailCell from '../components/detail/CustomEmailCell'
import SeminarLessonDetail from '../components/detail/SeminarLessonDetail'
import TabsSection from '../components/detail/TabsSection'
import { useSeminarDetail } from '../hooks/useSeminarDetail'

dayjs.extend(localizedFormat)
dayjs.locale('es')

const SeminarDetail = () => {
  const { t } = useTranslation(['global'])
  const location = useLocation()
  const myRef = useRef(null)
  const isMobile = useScreenWidth(700)
  const isLaptop = useScreenWidth(1024)

  const {
    seminarSelected,
    seminarlesson_selected,
    showForm,
    showFormSeminarLesson,
    registeredUsers,
    authUser,
    error,
    msg,
    loading,
    open,
    setOpen,
    favoriteState,
    currentTab,
    setCurrentTab,
    handleFavoriteClick,
    handleEditSeminar,
    handleAddSeminarLesson,
    handleBackToSeminarList,
    hasUserAccess,
    isAdminUser
  } = useSeminarDetail()

  const handleChangeTab = (_: any, newValue: number) => {
    setCurrentTab(newValue)
  }

  const getTableColumns = () => [
    {
      title: t('email'),
      field: 'email',
      render: (rowData: any) => <CustomEmailCell email={rowData.email} />
    },
    { title: t('phone'), field: 'phone', type: 'string' as const },
    { title: t('postal-code'), field: 'postalcode', type: 'string' as const },
    { title: t('town'), field: 'townName', type: 'string' as const },
    { title: t('registered-time'), field: 'registeredtime', type: 'string' as const }
  ]

  if (!seminarSelected) {
    return <ScreenLoading loadings={[loading]} />
  }

  const isWraped = false

  return (
    <Container id="seminar-detail-container" maxWidth={'xl'}>
      <VerifyAccessRoute visibility={seminarSelected.visibility} />

      {showForm && <SeminarModal />}

      {seminarlesson_selected === null && !showFormSeminarLesson ? (
        <ButtonBack data-seminardetail="goback" dataName="seminar" />
      ) : (
        <ButtonBack data-seminarlessondetail="goback" goBack={false} customFunction={handleBackToSeminarList} dataName="seminarlesson" />
      )}

      {seminarlesson_selected !== null && !showFormSeminarLesson && <SeminarLessonDetail handleOpen={setOpen} />}

      <Box
        display={isWraped ? 'flex' : 'block'}
        marginTop={seminarlesson_selected != null && !showFormSeminarLesson ? 4 : seminarSelected.lessons.length > 1 ? 0 : undefined}
        gap={isWraped ? 24 : 0}
      >
        <Box display="flex" mt={isWraped || isAdminUser ? 0 : 5} flexWrap={isMobile ? 'wrap' : 'nowrap'} gap={24}>
          <VipContainerHeroDetail maxWidth={isLaptop ? 500 : 960} width="100%" marginX="auto">
            <CoverSection seminar={seminarSelected} seminarlesson_selected={seminarlesson_selected} />
            <TabsSection
              currentTab={currentTab}
              onTabChange={handleChangeTab}
              isAdminUser={isAdminUser}
              seminarlesson_selected={seminarlesson_selected}
              showFormSeminarLesson={showFormSeminarLesson}
              seminarLessonsLength={seminarSelected.lessons.length}
              t={t}
            />
            <div ref={myRef}>
              <ContentTabs
                currentTab={currentTab}
                seminarSelected={seminarSelected}
                seminarlesson_selected={seminarlesson_selected}
                showFormSeminarLesson={showFormSeminarLesson}
                registeredUsers={registeredUsers}
                hasUserAccess={hasUserAccess}
                onOpenTicketModal={() => setOpen(true)}
                tableColumns={getTableColumns()}
                authUser={authUser}
                t={t}
              />
            </div>
          </VipContainerHeroDetail>
          <Box width={isMobile ? '100%' : 'auto'}>
            <DetailHeroPanel
              visibility={seminarSelected.visibility}
              status={seminarSelected.status}
              brand={'Anesi'}
              title={seminarSelected.title}
              description={isWraped ? seminarSelected.description : null}
              customButton={isWraped ? () => setOpen(true) : undefined}
              customButtonText={isWraped ? t('book-my-ticket') : null}
              edit={handleEditSeminar}
              add={handleAddSeminarLesson}
              onClickFavorite={handleFavoriteClick(seminarSelected.id)}
              favoriteState={favoriteState}
            />
            <DetailInfoPanel
              attributes={{
                item: [
                  {
                    id: 0,
                    type: 'date',
                    title: `${t('date')} ${t('live-event')}`,
                    color: 'grey6',
                    info: seminarSelected.datetime ? dayjs(seminarSelected.datetime).format('LLLL') : t('without-date')
                  }
                ]
              }}
            />
          </Box>
        </Box>
      </Box>

      {open && !location.pathname.includes('admin') && <TicketModal stateModal={open} handleClose={setOpen} />}
      <CustomSnackBar errors={[error]} msgs={[msg]} />
      <ScreenLoading loadings={[loading]} />
    </Container>
  )
}

export default SeminarDetail
